import {
  API_ERROR_CODES,
  isJwtAuthErrorCode,
  isProtectedRouteAuthErrorCode,
  parseApiError,
} from "@/lib/api/api-error";

export const PORTAL_NETWORK_ERROR =
  "We couldn't reach the server. Check your connection and try again.";

export const PORTAL_SESSION_ERROR =
  "Your session has expired. Please log in again.";

export const TEST_LOCATION_FAILURE =
  "We could not create this test location. Try again or contact support.";

export const TEST_LOCATION_STILL_PROVISIONING =
  "Provisioning is taking longer than expected. Refresh the page or try again later.";

export const SANDBOX_CREDENTIALS_FAILURE =
  "We could not request sandbox credentials. Try again.";

export const LIVE_CREDENTIALS_FAILURE =
  "We could not request live credentials. Try again.";

export const ADMIN_REVIEW_FAILURE =
  "We couldn't update this request. Please try again.";

export const ADMIN_REVOKE_FAILURE =
  "We couldn't revoke this access. Please try again.";

export const ADMIN_NOTE_FAILURE =
  "We couldn't save the note. Please try again.";

export const ADMIN_LIST_FAILURE =
  "We couldn't load access requests. Please try again.";

export const PORTAL_LIST_FAILURE =
  "Requests could not be loaded. Use Retry to try again.";

/** Sandbox API codes. Sentences stay here so pages do not inline them. */
export const SANDBOX_ERROR_MESSAGES: Record<string, string> = {
  "sandbox.location_limit_reached":
    "This sandbox already has two test locations.",
  "sandbox.workspace_not_found":
    "No sandbox workspace was found for this request. Try again after approval completes.",
  "sandbox.access_request_not_approved":
    "This request must be approved before you can create a test location.",
  "sandbox.no_ready_location":
    "Create a ready test location before you request API credentials.",
  "sandbox.location_not_found":
    "This test location could not be found. Refresh the page and try again.",
  "sandbox.invalid_input": "Check the location details and try again.",
  "sandbox.invalid_studio_type": "Check the location details and try again.",
  "sandbox.unauthorized": PORTAL_SESSION_ERROR,
};

/** Live credential request codes. */
export const LIVE_CREDENTIALS_ERROR_MESSAGES: Record<string, string> = {
  "accessrequests.live_credentials_disabled":
    "Live credentials are turned off in this environment.",
  "accessrequests.live_credentials_not_allowed":
    "Request live credentials only for an approved live request that is still active.",
  "accessrequests.not_found": "This access request could not be found.",
};

/** Admin approve, decline, revoke, and internal-note codes. */
export const ADMIN_ACTION_ERROR_MESSAGES: Record<string, string> = {
  "accessrequests.live_scope_unresolved":
    "Approve only after every location resolves.",
  "accessrequests.illegal_status_transition":
    "This request cannot be updated. Its status may already have changed.",
  "accessrequests.live_integrator_exists":
    "A live integrator already exists for this email.",
  "accessrequests.live_create_conflict":
    "A live integrator with this name already exists.",
  "accessrequests.live_create_bad_request":
    "The live integrator could not be created. Check the request and try again.",
  "accessrequests.invalid_confirmation": "Type Approve to confirm.",
  "accessrequests.invalid_rejection_reason":
    "Enter a decline reason between 10 and 1000 characters.",
  "accessrequests.revoke_not_allowed": "This request cannot be revoked.",
  "accessrequests.live_scope_already_revoked":
    "This live access is already revoked.",
  "accessrequests.live_revoke_failed":
    "Live access could not be revoked. Try again.",
  "accessrequests.invalid_internal_notes":
    "Notes must be 1000 characters or fewer.",
  "accessrequests.not_found": "This access request could not be found.",
};

export type PresentedApiError = {
  message: string;
  code?: string;
};

function withCode(message: string, code?: string): PresentedApiError {
  return code ? { message, code } : { message };
}

/**
 * Maps an API failure to a fixed sentence plus the stable `code`.
 * Never returns the response `message` body.
 */
export function presentApiError(
  err: unknown,
  options: { generic: string; byCode?: Record<string, string> },
): PresentedApiError {
  const parsed = parseApiError(err);
  const code = parsed.code?.trim() || undefined;

  if (parsed.isNetworkError) {
    return withCode(PORTAL_NETWORK_ERROR, code);
  }

  const mapped = code ? options.byCode?.[code] : undefined;
  if (mapped) {
    return withCode(mapped, code);
  }

  if (
    parsed.status === 401 ||
    isJwtAuthErrorCode(code) ||
    isProtectedRouteAuthErrorCode(code)
  ) {
    return withCode(PORTAL_SESSION_ERROR, code);
  }

  if (
    code === API_ERROR_CODES.internalServerError ||
    (parsed.status != null && parsed.status >= 500)
  ) {
    return withCode(options.generic, code);
  }

  return withCode(options.generic, code);
}
